import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function numberToPriceFormat(number = "") {
  number = number.split(".");
  return (
    number[0].replace(/(\d)(?=(\d\d)+\d$)/g, "$1,") +
    (number[1] ? "." + number[1] : "")
  );
}

// Enhanced formatting utilities for dashboard
export function formatCurrency(value, options = {}) {
  const { 
    decimals = 0, 
    symbol = '₹', 
    locale = 'en-IN',
    showSymbol = true 
  } = options;
  
  const num = Number(value) || 0;
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  
  const formatted = formatter.format(num);
  return showSymbol ? `${symbol}\u202F${formatted}` : formatted;
}

export function formatTons(value, decimals = 2) {
  const num = Number(value) || 0;
  return `${num.toFixed(decimals)} t`;
}

export function formatPercentage(value, decimals = 1, showSign = false) {
  const num = Number(value) || 0;
  const sign = showSign && num !== 0 ? (num > 0 ? '+' : '') : '';
  return `${sign}${num.toFixed(decimals)}%`;
}

export function formatTripCount(value) {
  return Math.round(Number(value) || 0).toString();
}

export function formatDisplayPercentage(value) {
  const num = Number(value) || 0;
  return `${num.toFixed(1)}%`;
}

// Semantic color logic for metrics
export function getSemanticColor(metricType, value, delta) {
  const deltaNum = Number(delta) || 0;
  
  switch (metricType) {
    case 'revenue':
    case 'collections':
    case 'inventory':
    case 'production':
    case 'trips':
      // Higher is better
      return deltaNum >= 0 ? 'positive' : 'negative';
    
    case 'expenses':
    case 'fuelPerTon':
      // Lower is better
      return deltaNum <= 0 ? 'positive' : 'negative';
    
    case 'collectionRatio':
      const num = Number(value) || 0;
      if (num > 100) return 'neutral'; // Avoid green for > 100%
      return num >= 80 ? 'positive' : num >= 50 ? 'neutral' : 'negative';
    
    default:
      return 'neutral';
  }
}

export function formatDelta(value, metricType, options = {}) {
  const { showPercentage = true, showSign = true } = options;
  const num = Number(value) || 0;
  
  if (num === 0) return null;
  
  const semanticColor = getSemanticColor(metricType, null, num);
  const arrow = num > 0 ? '▲' : '▼';
  const percentage = showPercentage ? `${Math.abs(num).toFixed(1)}%` : Math.abs(num).toFixed(1);
  const sign = showSign && showPercentage ? (num > 0 ? '+' : '') : '';
  
  return {
    text: `${arrow} ${sign}${percentage}`,
    color: semanticColor,
    value: num
  };
}

// Data validation and formatting
export function validateAndFormatCurrency(value, fallback = 0) {
  const num = Number(value);
  if (isNaN(num) || !isFinite(num)) {
    return formatCurrency(fallback);
  }
  return formatCurrency(num);
}

export function validateInventory(boulderInventory, onNegativeInventory) {
  if (boulderInventory === undefined || boulderInventory === null || boulderInventory === "") {
    return {
      value: 0,
      isNegative: false,
      warning: null
    };
  }
  
  const inventory = Number(boulderInventory);
  
  if (isNaN(inventory)) {
    return {
      value: 0,
      isNegative: false,
      warning: null
    };
  }
  
  if (inventory < 0 && onNegativeInventory && typeof onNegativeInventory === 'function') {
    try {
      onNegativeInventory(inventory);
    } catch (error) {
      console.debug('Inventory validation callback error:', error);
    }
  }
  
  return {
    value: inventory,
    isNegative: inventory < 0,
    warning: inventory < 0 ? 'Calculated inventory is negative — check opening stock or missing boulder receipts.' : null
  };
}

export function getCurrentDateFormatted() {
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit'
  }).format(new Date());
}

export function getAvatarKey(str) {
  if (!str || typeof str !== 'string') {
    return "UN";
  }

  // Extract first letter of each word, limit to 2 characters
  var matches = str.trim().match(/\b(\w)/g);
  if (!matches) {
    return "UN";
  }
  
  // Return maximum 2 characters, uppercase
  return matches.slice(0, 2).join("").toUpperCase();
}

export function formatDate(date, opts) {
  return new Intl.DateTimeFormat("en-US", {
    month: opts.month ?? "long",
    day: opts.day ?? "numeric",
    year: opts.year ?? "numeric",
    ...opts,
  }).format(new Date(date));
}

/**
 * Stole this from the @radix-ui/primitive
 * @see https://github.com/radix-ui/primitives/blob/main/packages/core/primitive/src/primitive.tsx
 */
export function composeEventHandlers(
  originalEventHandler,
  ourEventHandler,
  { checkForDefaultPrevented = true } = {}
) {
  return function handleEvent(event) {
    originalEventHandler?.(event);

    if (checkForDefaultPrevented === false || !event.defaultPrevented) {
      return ourEventHandler?.(event);
    }
  };
}
